import React, { useMemo, useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { useMsal } from '@azure/msal-react';
import {
    useAcceptNews,
    useAcquireNewsLock,
    useDeclineNews,
    useGetConfigurations,
    useReleaseNewsLock,
} from '../../api/NewsQueueApi';
import { toUiPublishState, UI_PUBLISH_STATES } from '../NewsQueueConstants';
import { ArticleViewProvider } from './ArticleViewContext';
import ArticleViewLayout from './components/ArticleViewLayout';
import useArticleEdits from './hooks/useArticleEdits';
import { EDITABLE_FIELDS } from './constants';
import { getRoundedCurrentLocalDateTime } from './utils';

export default function ArticleViewContainer({ article, open, onClose, isArticleLoading = false, panelManaged = false, deskShortcut = null }) {
    const theme = useTheme();
    const intl = useIntl();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectReasonExtra, setRejectReasonExtra] = useState('');

    const [publishReasonExtra, setPublishReasonExtra] = useState('');
    const [publishScheduleOpen, setPublishScheduleOpen] = useState(false);
    const [publishScheduleLocal, setPublishScheduleLocal] = useState(() =>
        getRoundedCurrentLocalDateTime()
    );
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
    const [publishConfirmMode, setPublishConfirmMode] = useState(null);

    const msal = useMsal();

    const editStartLabel = intl.formatMessage({ id: 'newsQueue.edit.start' });
    const editApplyLabel = intl.formatMessage({ id: 'newsQueue.edit.apply' });
    const editDiscardLabel = intl.formatMessage({ id: 'newsQueue.edit.discard' });
    const editRevertSavedLabel = intl.formatMessage({ id: 'newsQueue.edit.revertSaved' });

    const configurationsQuery = useGetConfigurations({ enabled: true });
    const acquireNewsLock = useAcquireNewsLock();
    const releaseNewsLock = useReleaseNewsLock();
    const acquireLockRef = React.useRef(acquireNewsLock.mutate);
    const releaseLockRef = React.useRef(releaseNewsLock.mutate);

    React.useEffect(() => {
        acquireLockRef.current = acquireNewsLock.mutate;
    }, [acquireNewsLock.mutate]);

    React.useEffect(() => {
        releaseLockRef.current = releaseNewsLock.mutate;
    }, [releaseNewsLock.mutate]);

    const activeAccount = msal?.instance?.getActiveAccount?.();
    const currentUserId =
        activeAccount?.username ?? activeAccount?.name ?? activeAccount?.localAccountId ?? null;

    const normalizeIdentity = (value) => String(value ?? '').trim().toLowerCase();
    const isLockActive = useMemo(() => {
        if (!article?.lock_expires_at) return false;
        const expiresAt = Date.parse(article.lock_expires_at);
        return !Number.isNaN(expiresAt) && expiresAt > Date.now();
    }, [article?.lock_expires_at]);

    const isLockedByOtherUser = useMemo(() => {
        if (!isLockActive) return false;
        if (!article?.locked_by) return false;
        if (!currentUserId) return true;
        return normalizeIdentity(article.locked_by) !== normalizeIdentity(currentUserId);
    }, [article?.locked_by, currentUserId, isLockActive]);

    const isLockedByCurrentUser = useMemo(() => {
        if (!isLockActive) return false;
        if (!article?.locked_by || !currentUserId) return false;
        return normalizeIdentity(article.locked_by) === normalizeIdentity(currentUserId);
    }, [article?.locked_by, currentUserId, isLockActive]);

    const declineNews = useDeclineNews({
        onSuccess: () => {
            if (article?.id) {
                releaseLockRef.current({ id: article.id });
            }
            setRejectOpen(false);
            setRejectReason('');
            setRejectReasonExtra('');
            onClose();
        },
    });

    const acceptNews = useAcceptNews({
        onSuccess: () => {
            if (article?.id) {
                releaseLockRef.current({ id: article.id });
            }
            setPublishConfirmOpen(false);
            setPublishConfirmMode(null);
            setPublishScheduleOpen(false);
            setPublishReasonExtra('');
            setPublishScheduleLocal('');
            onClose();
        },
    });

    const rejectionReasons = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        const raw = cfg?.feedback_categories;
        if (!Array.isArray(raw)) return [];
        return raw.filter((x) => typeof x === 'string' && x.trim().length > 0);
    }, [configurationsQuery?.data?.result]);

    const departmentCategories = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        const raw = cfg?.department_categories;
        if (!Array.isArray(raw)) return [];
        return Array.from(
            new Set(
                raw
                    .filter((x) => typeof x === 'string')
                    .map((x) => x.trim())
                    .filter((x) => x.length > 0)
            )
        );
    }, [configurationsQuery?.data?.result]);

    const targetMedias = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        
        const raw = cfg?.media_name_by_target_media;
        if (!raw || typeof raw !== 'object') return [];
        
        // Use raw media IDs directly from configuration keys
        return Object.keys(raw)
            .filter((key) => typeof key === 'string' && key.trim().length > 0)
            .map((key) => key.trim());
    }, [configurationsQuery?.data?.result]);

    const publishState = useMemo(() => toUiPublishState(article?.publish_state), [article?.publish_state]);
    const canEditFieldsByState =
        publishState !== UI_PUBLISH_STATES.ACCEPTED &&
        publishState !== UI_PUBLISH_STATES.REJECTED;
    const canEditFields = canEditFieldsByState && !isLockedByOtherUser && !isArticleLoading;

    const articleId = article?.id ?? null;

    const renewLock = React.useCallback(() => {
        if (!open || !articleId || isLockedByOtherUser) return;
        acquireLockRef.current({ id: articleId });
    }, [articleId, isLockedByOtherUser, open]);

    const editState = useArticleEdits({
        article,
        articleId,
        canEditFields,
        editableFields: EDITABLE_FIELDS,
        onPersistEdits: renewLock,
    });

    // Multi-publication configuration
    const getTargetMediaLabel = React.useCallback((value) => {
        const cfg = configurationsQuery?.data?.result;
        const key = String(value ?? '').trim();
        if (!key) return '-';
        return cfg?.media_name_by_target_media?.[key] ?? key;
    }, [configurationsQuery?.data?.result]);

    // Get available multi-publication medias based on context
    const availableMultiPublicationTargetMedias = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        if (!cfg) return [];

        const currentTargetMedia = editState.effectiveTargetMedia;

        // Priority: desk shortcut > media config > empty
        if (deskShortcut?.multi_publication_target_medias?.target_medias) {
            // deskShortcut is already a media collection object
            // Filter out the main target_media from the list
            return deskShortcut.multi_publication_target_medias.target_medias
                .filter(media => media !== currentTargetMedia);
        }

        // Use per-target_media configuration
        if (currentTargetMedia && cfg.multi_publication_target_medias_by_target_media) {
            const config = cfg.multi_publication_target_medias_by_target_media[currentTargetMedia];
            if (config?.target_medias) {
                // Filter out the main target_media from the list
                return config.target_medias.filter(media => media !== currentTargetMedia);
            }
        }

        return [];
    }, [configurationsQuery?.data?.result, deskShortcut, editState.effectiveTargetMedia]);

    // Get default multi-publication selections (enabled_by_default from config)
    const defaultMultiPublicationTargetMedias = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        if (!cfg) return [];

        const currentTargetMedia = editState.effectiveTargetMedia;

        // Priority: desk shortcut > media config
        if (deskShortcut?.multi_publication_target_medias?.enabled_by_default) {
            // deskShortcut is already a media collection object
            return deskShortcut.multi_publication_target_medias.target_medias
                .filter(media => media !== currentTargetMedia);
        }

        if (currentTargetMedia && cfg.multi_publication_target_medias_by_target_media) {
            const config = cfg.multi_publication_target_medias_by_target_media[currentTargetMedia];
            if (config?.enabled_by_default) {
                return config.target_medias.filter(media => media !== currentTargetMedia);
            }
        }

        return [];
    }, [configurationsQuery?.data?.result, deskShortcut, editState.effectiveTargetMedia]);

    React.useEffect(() => {
        if (!open || !articleId || isArticleLoading) return;
        acquireLockRef.current({ id: articleId });

        return () => {
            releaseLockRef.current({ id: articleId });
        };
    }, [articleId, open, isArticleLoading]);

    const ts = useMemo(() => {
        const value = article?.timestamp_article ?? article?.timestamp_created;
        const d = value ? new Date(value) : new Date();
        return Number.isNaN(d.getTime()) ? new Date() : d;
    }, [article?.timestamp_article, article?.timestamp_created]);

    const articleSources = useMemo(() => {
        const rawSources = article?.sources;
        if (!Array.isArray(rawSources)) return [];

        return rawSources
            .filter((source) => {
                if (!source) return false;
                const hasUrl = typeof source.url === 'string' && source.url.trim().length > 0;
                const hasContent =
                    typeof source.content === 'string' && source.content.trim().length > 0;
                return hasUrl || hasContent;
            })
            .map((source) => {
                const rawUrl = typeof source.url === 'string' ? source.url.trim() : '';
                const url = rawUrl.length > 0 ? rawUrl : null;
                const urlName =
                    typeof source.url_name === 'string' && source.url_name.trim().length > 0
                        ? source.url_name.trim()
                        : url;
                const description =
                    typeof source.description === 'string' && source.description.trim().length > 0
                        ? source.description.trim()
                        : null;
                const content =
                    typeof source.content === 'string' && source.content.trim().length > 0
                        ? source.content.trim()
                        : null;

                return { url, urlName, description, content };
            });
    }, [article?.sources]);

    const composedRejectFeedback = useMemo(() => {
        const extra = rejectReasonExtra.trim();
        if (rejectReason === 'other') return extra;

        const base = String(rejectReason ?? '').trim();
        if (!base) return extra;
        if (!extra) return base;
        return `${base}\n\n${extra}`;
    }, [rejectReason, rejectReasonExtra]);

    const composedPublishFeedback = useMemo(() => publishReasonExtra.trim(), [publishReasonExtra]);

    const publishScheduleIso = useMemo(() => {
        const raw = String(publishScheduleLocal ?? '').trim();
        if (!raw) return null;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return null;
        return d.toISOString();
    }, [publishScheduleLocal]);

    const publishScheduleValid = useMemo(() => {
        if (!publishScheduleIso) return false;
        const t = Date.parse(publishScheduleIso);
        if (Number.isNaN(t)) return false;
        return t > Date.now();
    }, [publishScheduleIso]);

    const publishConfirmText = useMemo(() => {
        if (publishConfirmMode === 'now') {
            return intl.formatMessage({ id: 'newsQueue.publish.confirmNow' });
        }
        if (publishConfirmMode === 'draft') {
            return intl.formatMessage({ id: 'newsQueue.publish.confirmDraft' });
        }
        return '';
    }, [intl, publishConfirmMode]);

    const publishErrorText = useMemo(() => {
        if (!acceptNews.isError) return '';

        const errorMessage = String(acceptNews.error?.message ?? '').toLowerCase();
        const isUnprocessableContent422 =
            errorMessage.includes('(422)') ||
            errorMessage.includes(' 422 ') ||
            errorMessage.includes('unprocessable content');

        if (isUnprocessableContent422) {
            return intl.formatMessage({
                id: 'newsQueue.publish.error.imageType',
            });
        }

        return intl.formatMessage({ id: 'newsQueue.publish.error.generic' });
    }, [acceptNews.error?.message, acceptNews.isError, intl]);

    // List responses omit article_body until getNews completes; an empty string is valid content.
    const isArticleBodyLoading = isArticleLoading || !Object.prototype.hasOwnProperty.call(
        article ?? {},
        EDITABLE_FIELDS.BODY
    );

    const handlers = useMemo(
        () => ({
            closeScheduleDialog: () => {
                if (acceptNews.isPending) return;
                acceptNews.reset();
                setPublishScheduleOpen(false);
            },
            closePublishConfirmDialog: () => {
                if (acceptNews.isPending) return;
                acceptNews.reset();
                setPublishConfirmOpen(false);
            },
            schedulePublish: () => {
                if (!publishScheduleIso) return;
                acceptNews.acceptScheduled({
                    news: editState.articleForPublish,
                    publishTimeIso: publishScheduleIso,
                    feedback: composedPublishFeedback,
                });
            },
            confirmPublish: () => {
                if (publishConfirmMode === 'now') {
                    acceptNews.acceptNow({
                        news: editState.articleForPublish,
                        feedback: composedPublishFeedback,
                    });
                    return;
                }
                if (publishConfirmMode === 'draft') {
                    acceptNews.acceptAsDraft({
                        news: editState.articleForPublish,
                        feedback: composedPublishFeedback,
                    });
                }
            },
            rejectArticle: () => {
                declineNews.decline({
                    news: article,
                    feedback: composedRejectFeedback,
                });
            },
        }),
        [
            acceptNews,
            article,
            composedPublishFeedback,
            composedRejectFeedback,
            declineNews,
            editState.articleForPublish,
            publishConfirmMode,
            publishScheduleIso,
        ]
    );

    if (!article) return null;

    const contextValue = {
        article,
        open,
        onClose,
        isMobile,
        canEditFields,
        isLockedByOtherUser,
        isLockedByCurrentUser,
        publishState,
        ts,
        targetMedias,
        articleSources,
        isArticleBodyLoading,
        editStartLabel,
        editApplyLabel,
        editDiscardLabel,
        editRevertSavedLabel,
        editableFields: EDITABLE_FIELDS,
        editState,
        configurationsQuery,
        departmentCategories,
        acceptNews,
        declineNews,
        rejectionReasons,
        rejectState: {
            rejectOpen,
            setRejectOpen,
            rejectReason,
            setRejectReason,
            rejectReasonExtra,
            setRejectReasonExtra,
        },
        publishStateUi: {
            publishReasonExtra,
            setPublishReasonExtra,
            publishScheduleOpen,
            setPublishScheduleOpen,
            publishScheduleLocal,
            setPublishScheduleLocal,
            publishScheduleIso,
            publishScheduleValid,
            publishConfirmOpen,
            setPublishConfirmOpen,
            publishConfirmMode,
            setPublishConfirmMode,
        },
        publishConfirmText,
        publishErrorText,
        handlers,
        deskShortcut,
        getTargetMediaLabel,
        availableMultiPublicationTargetMedias,
        defaultMultiPublicationTargetMedias,
    };

    return (
        <ArticleViewProvider value={contextValue}>
            <ArticleViewLayout panelManaged={panelManaged} />
        </ArticleViewProvider>
    );
}
